import React, { Fragment, useState, useEffect } from 'react'
import PatientService from '../../../../../services/api/patient.service'
import APIResponse from '../../../../templates/components/APIResponse'
import label from '../../../../../../assets/i18n/en.json'
import PatientSelector from '../../../../templates/components/PatientSelector'
import { Icon, Popup } from 'semantic-ui-react'

const CombinePatients = (props) => {
    const { onClose, patient, openBalance, creditBalance, form, tags } = props
    const [apiResponse, setApiResponse] = useState()
    const [isLoader, setIsLoader] = useState(false)
    const [fromPatient, setFromPatient] = useState(props.patient || null)
    const [toPatient, setToPatient] = useState(null)
    const [confirmPhrase, setConfirmPhrase] = useState('')
    const [validationError, setValidationError] = useState('')

    const onSuccess = (message) => {
        if (props.refresh) {
            props.refresh()
        }
        if (props.onClose) {
            props.onClose()
        }
        if (props.exitHandler) {
            props.exitHandler()
        }
    }

    const handleCombinePatients = () => {
        if (!fromPatient || !toPatient || confirmPhrase !== 'combine patients') {
            return
        }

        setIsLoader(true)
        const reqObj = {
            fromPatientId: fromPatient.id,
            toPatientId: toPatient.id
        }

        PatientService.combinePatients(reqObj)
            .then((res) => {
                setIsLoader(false)
                setApiResponse(res)
            })
            .catch((err) => {
                setIsLoader(false)
                setApiResponse(err)
                console.error(err)
            })
    }

    const renderPatientComparison = () => {
        if (!fromPatient || !toPatient) return null

        const fields = [
            { label: 'Name', getValue: (p) => `${p.firstName} ${p.lastName}` },
            { label: 'DOB', getValue: (p) => new Date(p.dob).toLocaleDateString() },
            { label: 'Email', getValue: (p) => p.email },
            { label: 'Mobile', getValue: (p) => p.mobile },
            { label: 'SSN', getValue: (p) => p.ssn },
            {
                label: 'Address', getValue: (p) => {
                    if (!p.address) return 'No address';
                    const addr1 = p.address.addressLine1 || '';
                    const addr2 = p.address.addressLine2 || '';
                    const city = p.address.city || '';
                    const state = p.address.state || '';
                    const postal = p.address.postalCode || '';
                    
                    return [
                        addr1,
                        addr2,
                        city && (state || postal) ? city + ',' : city,
                        state && postal ? state + ' ' : state,
                        postal
                    ].filter(Boolean).join(' ');
                }
            }
        ]

        return (
            <div className="mt-3 mb-4">
                <h5>Patient Comparison</h5>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Field</th>
                            <th>From Patient</th>
                            <th>To Patient</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field, index) => (
                            <tr key={index}>
                                <td>{field.label}</td>
                                <td>{field.getValue(fromPatient)}</td>
                                <td>{field.getValue(toPatient)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <Fragment>
            {isLoader && <div className="ui">
                <div className="ui active dimmer">
                    <div className="ui indeterminate text loader">{label.common.processing}</div>
                </div>
            </div>}

            <div className="d-flex flex-column">
                <div className="alert alert-warning">
                    <strong>Warning:</strong> All records From Patient will be moved To Patient.
                    This action is irreversible.
                </div>

                <div className="mb-3">
                    <label>From Patient (to be merged) {((openBalance && typeof openBalance === 'number' && openBalance > 0) || 
                        (creditBalance?.total && creditBalance.total > 0)) && (
                        <Popup
                            trigger={<Icon name="exclamation circle" color="orange" />}
                            content={
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    {openBalance && typeof openBalance === 'number' && openBalance > 0 && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <strong>Open Balance: ${Number(openBalance).toFixed(2)}</strong>
                                        </div>
                                    )}
                                    {creditBalance?.total > 0 && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <strong>Credit Balance: ${Number(creditBalance.total).toFixed(2)}</strong>
                                        </div>
                                    )}
                                </div>
                            }
                            position="top center"
                            hoverable
                        />
                    )}</label>
                    
                    {fromPatient && <PatientSelector
                        onSelectedPatient={(patient) => {
                            if (!patient) {
                                setFromPatient(null)
                                setValidationError('')
                                return
                            }
                            if (toPatient && toPatient.id === patient.id) {
                                setValidationError('From Patient and To Patient cannot be the same')
                                setFromPatient(null)
                            } else {
                                setValidationError('')
                                setFromPatient(patient)
                            }
                        }}
                        showAddPatient={false}
                        disabled={!!fromPatient}
                        defaultValue={fromPatient ?? null}
                        showEmail={true}
                    />}
                </div>

                <div className="mb-3">
                    <label>To Patient (target)</label>
                    <PatientSelector
                        onSelectedPatient={(patient) => {
                            if (!patient) {
                                setToPatient(null)
                                setValidationError('')
                                return
                            }
                            if (fromPatient && fromPatient.id === patient.id) {
                                setValidationError('From Patient and To Patient cannot be the same')
                                setToPatient(null)
                            } else {
                                setValidationError('')
                                setToPatient(patient)
                            }
                        }}
                        showAddPatient={false}
                        showEmail={true}
                    />
                    {validationError && (
                        <div className="alert alert-danger mt-2 mb-0 p-1">
                            <i className='icon exclamation triangle'/>{validationError}
                        </div>
                    )}
                </div>

                {renderPatientComparison()}

                <div className="mb-3 required field">
                    <label>Type "combine patients" to confirm</label>
                    <input
                        type="text"
                        className="form-control"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value.toLowerCase())}
                        placeholder="Type 'combine patients' to enable submission"
                    />
                </div>

                <div className="d-flex justify-content-between mt-3">
                    <button
                        className="btn btn-primary"
                        disabled={!fromPatient || !toPatient || confirmPhrase !== 'combine patients'}
                        onClick={handleCombinePatients}
                    >
                        Combine Patients
                    </button>
                    <button className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            <APIResponse apiResponse={apiResponse} onSuccess={onSuccess} toastOnSuccess={true} />
        </Fragment>
    )
}

export default CombinePatients